import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getTopicPages, topicPageAct, type TopicPageRow } from './api'
import { useEditorialTitle } from './titleContext'
import { etDate } from './labels'
import { ACTION_LABEL, CONFIRM, STATE_LABEL, actionPath, actionsFor, articleCount, hiddenBanner, showHiddenBanner, type TopicAction, type TopicState } from './topicPages'
import { describeError } from '../lib/describeError'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-3.5 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const btnPrimary = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-3.5 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 disabled:opacity-50 ${focus}`
const chip = 'inline-flex h-6 items-center rounded-full px-2.5 font-ui text-[12px] font-semibold whitespace-nowrap'
const STATE_CLASS: Record<TopicState, string> = {
  proposed: 'bg-amber-wash text-amber-deep',
  hidden: 'bg-ink-600 text-paper-dim',
  live: 'bg-radar-green/15 text-radar-green',
  retired: 'bg-ink-600 text-paper-faint',
}

export function TopicPagesPage() {
  const { publicationSlug } = useEditorialTitle()
  const qc = useQueryClient()
  const q = useInfiniteQuery({
    queryKey: ['editorial', 'topic-pages', publicationSlug],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => getTopicPages(publicationSlug, pageParam),
    getNextPageParam: (last) => (typeof last.next_offset === 'number' ? last.next_offset : undefined),
  })
  const act = useMutation({
    mutationFn: ({ row, action }: { row: TopicPageRow; action: Exclude<TopicAction, 'preview'> }) => topicPageAct(actionPath(row.state, row.id, action)),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['editorial', 'topic-pages', publicationSlug] }) },
  })
  const pages = q.data?.pages.flatMap((p) => p.pages) ?? []
  const hiddenSince = q.data?.pages[0]?.hidden_since

  const run = (row: TopicPageRow, action: Exclude<TopicAction, 'preview'>) => {
    const msg = CONFIRM[action]
    if (msg && !window.confirm(msg)) return
    act.mutate({ row, action })
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-1.5">
        <h1 className="m-0 font-serif text-[34px] font-medium leading-tight text-paper">Topic pages</h1>
        <p className="m-0 max-w-[720px] font-ui text-paper-dim">Landing pages that gather everything on one subject, such as “P-51 Mustang” or “Doolittle Raid”. The agent proposes and fills them; you decide which ones readers see.</p>
      </header>
      {q.data && showHiddenBanner(pages) && hiddenSince && (
        <div role="status" className="rounded-[10px] bg-ink-600 px-4 py-3.5 font-ui text-[14px] text-paper"><strong>{hiddenBanner(etDate(hiddenSince))}</strong></div>
      )}
      {q.isLoading && <p className="m-0 font-ui text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 font-ui text-radar-red">{describeError(q.error)}</p>}
      {act.isError && <p role="alert" className="m-0 font-ui text-radar-red">{describeError(act.error)}</p>}
      {q.data && pages.length === 0 && <p className="m-0 font-ui text-paper-dim">No topic pages yet.</p>}
      <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(300px,100%),1fr))]">
        {pages.map((p) => {
          const state = (p.state in STATE_LABEL ? p.state : 'hidden') as TopicState
          return (
            <article key={`${p.state}-${p.id}`} className="flex flex-col gap-2.5 rounded-xl border border-ink-500 bg-ink-700 p-4">
              <div className="flex items-center justify-between gap-2">
                <strong className="font-serif text-[20px] font-medium text-paper">{p.name}</strong>
                <span className={`${chip} ${STATE_CLASS[state]}`}>{STATE_LABEL[state]}</span>
              </div>
              {p.slug_taken && <span className="font-ui text-[13px] text-amber-deep">A page already lives at /topic/{p.slug}.</span>}
              <span className="font-ui text-[13px] text-paper-dim">
                {articleCount(p.article_count)}{p.updated_at ? ` · updated ${etDate(p.updated_at)} ET` : ''}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {actionsFor(p.state, p.slug_taken).map((a) =>
                  a === 'preview'
                    ? (p.live_url && <a key={a} href={p.live_url} target="_blank" rel="noopener noreferrer" className={btn}>{ACTION_LABEL[a]}</a>)
                    : <button key={a} type="button" className={a === 'approve' ? btnPrimary : btn} disabled={act.isPending} onClick={() => run(p, a)}>{ACTION_LABEL[a]}</button>)}
              </div>
            </article>
          )
        })}
      </div>
      {q.hasNextPage && (
        <button type="button" className={`${btn} self-start`} disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
          {q.isFetchingNextPage ? 'Loading…' : 'Show more'}
        </button>
      )}
    </div>
  )
}
